// Generat automat din README.md - Enhanced cu funcționalități interactive
import React, { useState, useEffect } from 'react';
import { Target, Users, TrendingUp, Award, Plus, Search, Filter, MoreVertical, CheckCircle, Clock, AlertCircle, X, Info, AlertTriangle, Edit, Trash2 } from 'lucide-react';
import Card from '../../components/Card';
import Table from '../../components/Table';
import Badge from '../../components/Badge';
import Modal from '../../components/Modal';

// Toast Component
const Toast = ({ message, type, onClose }) => {
  const icons = {
    success: CheckCircle,
    error: AlertTriangle,
    warning: AlertTriangle,
    info: Info
  };

  const colors = {
    success: 'bg-green-500',
    error: 'bg-red-500', 
    warning: 'bg-yellow-500',
    info: 'bg-blue-500'
  };

  const Icon = icons[type];

  useEffect(() => {
    const timer = setTimeout(() => {
      onClose();
    }, 3000);

    return () => clearTimeout(timer);
  }, [onClose]);

  return (
    <div className={`${colors[type]} text-white p-4 rounded-lg shadow-lg flex items-center space-x-3 animate-pulse`}>
      <Icon className="w-5 h-5" />
      <span>{message}</span>
      <button onClick={onClose}>
        <X className="w-4 h-4" />
      </button>
    </div>
  );
};

// Toast Hook
const useToast = () => {
  const [toasts, setToasts] = useState([]);

  const showToast = (message, type = 'info') => {
    const id = Date.now();
    const newToast = { id, message, type };
    
    setToasts(prev => [...prev, newToast]);
  };

  const removeToast = (id) => {
    setToasts(prev => prev.filter(toast => toast.id !== id));
  };

  return { toasts, showToast, removeToast };
};

// Validation utilities
const validators = {
  required: (value) => {
    if (!value || value.toString().trim() === '') {
      return 'Acest câmp este obligatoriu';
    }
    return null;
  },

  minLength: (min) => (value) => {
    if (value && value.length < min) {
      return `Minimum ${min} caractere`;
    }
    return null;
  },

  dateAfterToday: (value) => {
    if (value && new Date(value) <= new Date()) {
      return 'Data trebuie să fie în viitor';
    }
    return null;
  }
};

// Validation Hook
const useValidation = (formData, rules) => {
  const [errors, setErrors] = useState({});

  const validate = () => {
    const newErrors = {};
    
    Object.keys(rules).forEach(field => {
      const fieldRules = rules[field];
      const value = formData[field];
      
      fieldRules.forEach(rule => {
        const error = rule(value);
        if (error && !newErrors[field]) {
          newErrors[field] = error;
        }
      });
    });

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const clearErrors = () => setErrors({});

  return { errors, validate, clearErrors };
};

const EvaluarePerformanta = () => {
  const [showObjectiveModal, setShowObjectiveModal] = useState(false);
  const [showFeedbackModal, setShowFeedbackModal] = useState(false);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterDepartment, setFilterDepartment] = useState('all');
  const [isLoading, setIsLoading] = useState(false);

  // State pentru obiective
  const [objectives, setObjectives] = useState([
    {
      id: 1,
      employeeId: 1,
      employeeName: 'Ion Popescu',
      title: 'Implementare sistem CRM',
      description: 'Dezvoltarea modulului de gestionare clienți',
      category: 'Dezvoltare',
      priority: 'Înaltă',
      targetDate: '2024-04-15',
      progress: 80,
      status: 'În progres',
      createdDate: '2024-03-01'
    },
    {
      id: 2,
      employeeId: 1,
      employeeName: 'Ion Popescu',
      title: 'Optimizare performanță aplicație',
      description: 'Îmbunătățirea timpilor de răspuns cu 30%',
      category: 'Optimizare',
      priority: 'Medie',
      targetDate: '2024-05-01',
      progress: 45,
      status: 'În progres',
      createdDate: '2024-03-05'
    }
  ]);

  // State pentru employees
  const [employees] = useState([
    {
      id: 1,
      name: 'Ion Popescu',
      position: 'Senior Developer',
      department: 'IT',
      currentScore: 9.2,
      lastEvaluation: '2024-02-15',
      objectivesCompleted: 8,
      totalObjectives: 10,
      status: 'Excelent',
      trend: 'up'
    },
    {
      id: 2,
      name: 'Maria Ionescu',
      position: 'UI/UX Designer',
      department: 'Design',
      currentScore: 8.7,
      lastEvaluation: '2024-02-20',
      objectivesCompleted: 6,
      totalObjectives: 8,
      status: 'Foarte bine',
      trend: 'up'
    },
    {
      id: 3,
      name: 'Andrei Georgescu',
      position: 'Marketing Manager',
      department: 'Marketing',
      currentScore: 7.8,
      lastEvaluation: '2024-02-10',
      objectivesCompleted: 5,
      totalObjectives: 9,
      status: 'Bine',
      trend: 'stable'
    }
  ]);

  // Form state pentru obiective
  const [objectiveFormData, setObjectiveFormData] = useState({
    employeeId: '',
    title: '',
    description: '',
    category: 'Dezvoltare',
    priority: 'Medie',
    targetDate: ''
  });

  // Toast system
  const { toasts, showToast, removeToast } = useToast();

  // Form validation rules pentru obiective
  const objectiveValidationRules = {
    employeeId: [validators.required],
    title: [validators.required, validators.minLength(5)],
    description: [validators.required, validators.minLength(10)],
    targetDate: [validators.required, validators.dateAfterToday]
  };

  const { errors, validate, clearErrors } = useValidation(objectiveFormData, objectiveValidationRules);

  // Calculare statistici dinamice
  const performanceStats = {
    totalEvaluations: employees.length,
    completedThisQuarter: objectives.filter(obj => obj.status === 'Finalizat').length,
    averageScore: employees.length > 0 ? (employees.reduce((sum, emp) => sum + emp.currentScore, 0) / employees.length).toFixed(1) : 0,
    improvementRate: "+15%"
  };

  // Event handlers
  const handleEmployeeClick = (employee) => {
    setSelectedEmployee(employee);
    setShowFeedbackModal(true);
  };

  const handleObjectiveFormChange = (field, value) => {
    setObjectiveFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      clearErrors();
    }
  };

  const resetObjectiveForm = () => {
    setObjectiveFormData({
      employeeId: '',
      title: '',
      description: '',
      category: 'Dezvoltare',
      priority: 'Medie',
      targetDate: ''
    });
    clearErrors();
  };

  const handleSaveObjective = async () => {
    if (!validate()) {
      showToast('Completează toate câmpurile obligatorii!', 'error');
      return;
    }

    setIsLoading(true);

    try {
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 1000));

      // Find employee name
      const employee = employees.find(emp => emp.id === parseInt(objectiveFormData.employeeId));
      
      // Create new objective
      const newObjective = {
        id: objectives.length + 1,
        ...objectiveFormData,
        employeeId: parseInt(objectiveFormData.employeeId),
        employeeName: employee?.name || 'Unknown',
        progress: 0,
        status: 'În progres',
        createdDate: new Date().toISOString().split('T')[0]
      };

      // Add to objectives list
      setObjectives(prev => [...prev, newObjective]);

      // Success feedback
      showToast(`Obiectiv "${newObjective.title}" a fost creat cu succes!`, 'success');

      // Reset and close
      resetObjectiveForm();
      setShowObjectiveModal(false);

    } catch (error) {
      showToast('A apărut o eroare la salvarea obiectivului!', 'error');
    } finally {
      setIsLoading(false);
    }
  };

  const handleDeleteObjective = (objectiveId) => {
    const objective = objectives.find(obj => obj.id === objectiveId);
    if (window.confirm(`Ești sigur că vrei să ștergi obiectivul "${objective?.title}"?`)) {
      setObjectives(prev => prev.filter(obj => obj.id !== objectiveId));
      showToast('Obiectiv șters cu succes!', 'success');
    }
  };

  const handleUpdateObjectiveProgress = (objectiveId, newProgress) => {
    setObjectives(prev => prev.map(obj => 
      obj.id === objectiveId 
        ? { 
            ...obj, 
            progress: newProgress,
            status: newProgress >= 100 ? 'Finalizat' : newProgress > 0 ? 'În progres' : 'Nou'
          } 
        : obj
    ));
    showToast('Progres actualizat!', 'success');
  };

  // Helper functions
  const getStatusColor = (status) => {
    const colors = {
      'Excelent': 'success',
      'Foarte bine': 'success', 
      'Bine': 'warning',
      'Satisfăcător': 'warning',
      'Necesită îmbunătățire': 'error'
    };
    return colors[status] || 'default';
  };

  const getPriorityColor = (priority) => {
    const colors = {
      'Înaltă': 'bg-red-100 text-red-800 dark:bg-red-900/20 dark:text-red-400',
      'Medie': 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/20 dark:text-yellow-400',
      'Scăzută': 'bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-400'
    };
    return colors[priority] || 'bg-gray-100 text-gray-800 dark:bg-gray-900/20 dark:text-gray-400';
  };

  const getProgressColor = (progress) => {
    if (progress >= 80) return 'bg-green-500';
    if (progress >= 50) return 'bg-yellow-500';
    return 'bg-red-500';
  };

  // Table columns pentru angajați
  const employeeColumns = [
    { 
      header: 'Angajat', 
      key: 'name',
      render: (value, row) => (
        <div>
          <p className="font-semibold text-gray-900 dark:text-white">{value}</p>
          <p className="text-sm text-gray-600 dark:text-gray-400">{row.position}</p>
        </div>
      )
    },
    { header: 'Department', key: 'department' },
    { 
      header: 'Scor Curent', 
      key: 'currentScore',
      render: (value) => (
        <div className="flex items-center space-x-2">
          <span className="font-semibold">{value}</span>
          <div className="w-16 bg-gray-200 rounded-full h-2">
            <div 
              className={`h-2 rounded-full ${value >= 8 ? 'bg-green-500' : value >= 6 ? 'bg-yellow-500' : 'bg-red-500'}`}
              style={{ width: `${(value / 10) * 100}%` }}
            ></div>
          </div>
        </div>
      )
    },
    { 
      header: 'Obiective', 
      key: 'objectivesCompleted',
      render: (value, row) => `${value}/${row.totalObjectives}`
    },
    { 
      header: 'Status', 
      key: 'status',
      render: (value) => <Badge variant={getStatusColor(value)}>{value}</Badge>
    },
    { header: 'Ultima Evaluare', key: 'lastEvaluation' }
  ];

  // Table columns pentru obiective
  const objectiveColumns = [
    { 
      header: 'Obiectiv', 
      key: 'title',
      render: (value, row) => (
        <div>
          <p className="font-semibold text-gray-900 dark:text-white">{value}</p>
          <p className="text-sm text-gray-600 dark:text-gray-400">{row.employeeName}</p>
        </div>
      )
    },
    { 
      header: 'Categorie', 
      key: 'category'
    },
    { 
      header: 'Prioritate', 
      key: 'priority',
      render: (value) => (
        <span className={`inline-block px-2 py-1 text-sm font-medium rounded-full ${getPriorityColor(value)}`}>
          {value}
        </span>
      )
    },
    { 
      header: 'Progres', 
      key: 'progress',
      render: (value, row) => (
        <div className="flex items-center space-x-2">
          <div className="w-20 bg-gray-200 rounded-full h-2">
            <div 
              className={`h-2 rounded-full ${getProgressColor(value)}`}
              style={{ width: `${value}%` }}
            ></div>
          </div>
          <span className="text-sm font-medium">{value}%</span>
        </div>
      )
    },
    { header: 'Deadline', key: 'targetDate' },
    { 
      header: 'Acțiuni', 
      key: 'actions',
      render: (_, row) => (
        <div className="flex items-center space-x-2">
          <button 
            onClick={() => handleUpdateObjectiveProgress(row.id, Math.min(100, row.progress + 25))}
            className="text-green-600 hover:text-green-800 dark:text-green-400"
            title="Avansează progresul"
          >
            <CheckCircle className="w-4 h-4" />
          </button>
          <button 
            onClick={() => handleDeleteObjective(row.id)}
            className="text-red-600 hover:text-red-800 dark:text-red-400"
            title="Șterge obiectiv"
          >
            <Trash2 className="w-4 h-4" />
          </button>
        </div>
      )
    }
  ];

  // Filter data
  const filteredEmployees = employees.filter(employee => {
    const matchesSearch = employee.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         employee.position.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesDepartment = filterDepartment === 'all' || employee.department === filterDepartment;
    return matchesSearch && matchesDepartment;
  });

  const filteredObjectives = objectives.filter(objective => {
    const matchesSearch = objective.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         objective.employeeName.toLowerCase().includes(searchTerm.toLowerCase());
    return matchesSearch;
  });

  // Get unique departments for filter
  const departments = [...new Set(employees.map(emp => emp.department))];

  return (
    <div className="space-y-6">
      {/* Toast Notifications */}
      <div className="fixed top-4 right-4 space-y-2 z-50">
        {toasts.map((toast) => (
          <Toast
            key={toast.id}
            message={toast.message}
            type={toast.type}
            onClose={() => removeToast(toast.id)}
          />
        ))}
      </div>

      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white">Evaluare Performanță</h1>
          <p className="text-gray-600 dark:text-gray-400 mt-2">
            Sistem complet cu obiective și feedback 360° AI pentru evaluarea performanței angajaților
          </p>
        </div>

        <button
          onClick={() => setShowObjectiveModal(true)}
          className="flex items-center space-x-2 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
        >
          <Plus className="w-4 h-4" />
          <span>Obiectiv Nou</span>
        </button>
      </div>

      {/* Statistici overview */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card>
          <div className="flex items-center space-x-3">
            <Users className="w-8 h-8 text-blue-600" />
            <div>
              <p className="text-2xl font-bold text-gray-900 dark:text-white">{performanceStats.totalEvaluations}</p>
              <p className="text-sm text-gray-600 dark:text-gray-400">Total Evaluări</p>
            </div>
          </div>
        </Card>

        <Card>
          <div className="flex items-center space-x-3">
            <CheckCircle className="w-8 h-8 text-green-600" />
            <div>
              <p className="text-2xl font-bold text-gray-900 dark:text-white">{performanceStats.completedThisQuarter}</p>
              <p className="text-sm text-gray-600 dark:text-gray-400">Finalizate Q1</p>
            </div>
          </div>
        </Card>

        <Card>
          <div className="flex items-center space-x-3">
            <Award className="w-8 h-8 text-yellow-600" />
            <div>
              <p className="text-2xl font-bold text-gray-900 dark:text-white">{performanceStats.averageScore}</p>
              <p className="text-sm text-gray-600 dark:text-gray-400">Scor Mediu</p>
            </div>
          </div>
        </Card>

        <Card>
          <div className="flex items-center space-x-3">
            <TrendingUp className="w-8 h-8 text-purple-600" />
            <div>
              <p className="text-2xl font-bold text-gray-900 dark:text-white">{performanceStats.improvementRate}</p>
              <p className="text-sm text-gray-600 dark:text-gray-400">Îmbunătățire</p>
            </div>
          </div>
        </Card>
      </div>

      {/* Tabs pentru Angajați și Obiective */}
      <Card>
        <div className="border-b border-gray-200 dark:border-gray-700">
          <nav className="-mb-px flex space-x-8">
            <button className="border-primary-500 text-primary-600 flex items-center space-x-2 py-2 px-1 border-b-2 font-medium text-sm">
              <Users className="w-4 h-4" />
              <span>Evaluări Angajați</span>
            </button>
          </nav>
        </div>

        <div className="mt-6 space-y-6">
          {/* Filtrare și căutare */}
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="flex-1 relative">
              <Search className="w-5 h-5 absolute left-3 top-3 text-gray-400" />
              <input
                type="text"
                placeholder="Caută angajați..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 dark:bg-gray-700 dark:text-white"
              />
            </div>
            <select
              value={filterDepartment}
              onChange={(e) => setFilterDepartment(e.target.value)}
              className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 dark:bg-gray-700 dark:text-white"
            >
              <option value="all">Toate departamentele</option>
              {departments.map(dept => (
                <option key={dept} value={dept}>{dept}</option>
              ))}
            </select>
          </div>
          
          <Table
            columns={employeeColumns}
            data={filteredEmployees}
            onRowClick={(row) => handleEmployeeClick(row)}
          />
        </div>
      </Card>

      {/* Tabel obiective */}
      <Card>
        <div className="mb-6">
          <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">Obiective Active</h3>
          <p className="text-gray-600 dark:text-gray-400">Management obiective și tracking progres în timp real</p>
        </div>

        <Table
          columns={objectiveColumns}
          data={filteredObjectives}
        />
      </Card>

      {/* Modal adăugare obiectiv */}
      {showObjectiveModal && (
        <Modal
          isOpen={showObjectiveModal}
          onClose={() => {
            setShowObjectiveModal(false);
            resetObjectiveForm();
          }}
          title="Adaugă Obiectiv Nou"
        >
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Angajat *
              </label>
              <select
                value={objectiveFormData.employeeId}
                onChange={(e) => handleObjectiveFormChange('employeeId', e.target.value)}
                className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 dark:bg-gray-700 dark:text-white ${
                  errors.employeeId ? 'border-red-500' : 'border-gray-300 dark:border-gray-600'
                }`}
              >
                <option value="">Selectează angajat</option>
                {employees.map(employee => (
                  <option key={employee.id} value={employee.id}>
                    {employee.name} - {employee.position}
                  </option>
                ))}
              </select>
              {errors.employeeId && <p className="text-red-500 text-xs mt-1">{errors.employeeId}</p>}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Titlu Obiectiv *
              </label>
              <input
                type="text"
                value={objectiveFormData.title}
                onChange={(e) => handleObjectiveFormChange('title', e.target.value)}
                className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 dark:bg-gray-700 dark:text-white ${
                  errors.title ? 'border-red-500' : 'border-gray-300 dark:border-gray-600'
                }`}
                placeholder="Ex: Implementare funcționalitate nouă"
              />
              {errors.title && <p className="text-red-500 text-xs mt-1">{errors.title}</p>}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Descriere *
              </label>
              <textarea
                value={objectiveFormData.description}
                onChange={(e) => handleObjectiveFormChange('description', e.target.value)}
                rows={3}
                className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 dark:bg-gray-700 dark:text-white ${
                  errors.description ? 'border-red-500' : 'border-gray-300 dark:border-gray-600'
                }`}
                placeholder="Descrierea detaliată a obiectivului..."
              />
              {errors.description && <p className="text-red-500 text-xs mt-1">{errors.description}</p>}
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Categorie
                </label>
                <select
                  value={objectiveFormData.category}
                  onChange={(e) => handleObjectiveFormChange('category', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 dark:bg-gray-700 dark:text-white"
                >
                  <option value="Dezvoltare">Dezvoltare</option>
                  <option value="Optimizare">Optimizare</option>
                  <option value="Management">Management</option>
                  <option value="Training">Training</option>
                  <option value="Colaborare">Colaborare</option>
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Prioritate
                </label>
                <select
                  value={objectiveFormData.priority}
                  onChange={(e) => handleObjectiveFormChange('priority', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 dark:bg-gray-700 dark:text-white"
                >
                  <option value="Scăzută">Scăzută</option>
                  <option value="Medie">Medie</option>
                  <option value="Înaltă">Înaltă</option>
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Data Țintă *
                </label>
                <input
                  type="date"
                  value={objectiveFormData.targetDate}
                  onChange={(e) => handleObjectiveFormChange('targetDate', e.target.value)}
                  className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 dark:bg-gray-700 dark:text-white ${
                    errors.targetDate ? 'border-red-500' : 'border-gray-300 dark:border-gray-600'
                  }`}
                />
                {errors.targetDate && <p className="text-red-500 text-xs mt-1">{errors.targetDate}</p>}
              </div>
            </div>
            
            <div className="flex justify-end space-x-3 pt-4 border-t dark:border-gray-700">
              <button
                onClick={() => {
                  setShowObjectiveModal(false);
                  resetObjectiveForm();
                }}
                className="px-4 py-2 text-gray-600 bg-gray-100 hover:bg-gray-200 dark:bg-gray-700 dark:text-gray-400 dark:hover:bg-gray-600 rounded-lg transition-colors"
              >
                Anulează
              </button>
              <button
                onClick={handleSaveObjective}
                disabled={isLoading}
                className={`px-4 py-2 rounded-lg transition-colors flex items-center space-x-2 ${
                  isLoading
                    ? 'bg-gray-400 cursor-not-allowed'
                    : 'bg-primary-600 hover:bg-primary-700 text-white'
                }`}
              >
                {isLoading && (
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                )}
                <span>{isLoading ? 'Se salvează...' : 'Creează Obiectiv'}</span>
              </button>
            </div>
          </div>
        </Modal>
      )}

      {/* Modal detalii angajat */}
      {showFeedbackModal && selectedEmployee && (
        <Modal
          isOpen={showFeedbackModal}
          onClose={() => setShowFeedbackModal(false)}
          title={`Evaluare Performanță - ${selectedEmployee.name}`}
        >
          <div className="space-y-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <div className="space-y-4">
                <h3 className="font-semibold text-gray-900 dark:text-white">Informații Angajat</h3>
                
                <div className="flex items-center space-x-3">
                  <Users className="w-5 h-5 text-gray-400" />
                  <div>
                    <p className="text-sm text-gray-500">Poziție</p>
                    <p className="font-medium">{selectedEmployee.position}</p>
                  </div>
                </div>

                <div className="flex items-center space-x-3">
                  <Target className="w-5 h-5 text-gray-400" />
                  <div>
                    <p className="text-sm text-gray-500">Department</p>
                    <p className="font-medium">{selectedEmployee.department}</p>
                  </div>
                </div>
              </div>

              <div className="space-y-4">
                <h3 className="font-semibold text-gray-900 dark:text-white">Performanță Actuală</h3>
                
                <div className="flex items-center space-x-3">
                  <Award className="w-5 h-5 text-gray-400" />
                  <div>
                    <p className="text-sm text-gray-500">Scor Performanță</p>
                    <div className="flex items-center space-x-2">
                      <span className="font-bold text-lg">{selectedEmployee.currentScore}/10</span>
                      <Badge variant={getStatusColor(selectedEmployee.status)}>
                        {selectedEmployee.status}
                      </Badge>
                    </div>
                  </div>
                </div>

                <div className="flex items-center space-x-3">
                  <CheckCircle className="w-5 h-5 text-gray-400" />
                  <div>
                    <p className="text-sm text-gray-500">Obiective</p>
                    <p className="font-medium">
                      {selectedEmployee.objectivesCompleted}/{selectedEmployee.totalObjectives} completate
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <div>
              <h3 className="font-semibold text-gray-900 dark:text-white mb-3">Obiective Active</h3>
              <div className="space-y-2">
                {objectives
                  .filter(obj => obj.employeeId === selectedEmployee.id)
                  .map(objective => (
                    <div key={objective.id} className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-800 rounded-lg">
                      <div className="flex-1">
                        <p className="font-medium">{objective.title}</p>
                        <div className="flex items-center space-x-2 mt-1">
                          <div className="w-24 bg-gray-200 rounded-full h-2">
                            <div 
                              className={`h-2 rounded-full ${getProgressColor(objective.progress)}`}
                              style={{ width: `${objective.progress}%` }}
                            ></div>
                          </div>
                          <span className="text-sm text-gray-600">{objective.progress}%</span>
                        </div>
                      </div>
                      <span className={`inline-block px-2 py-1 text-xs rounded-full ${getPriorityColor(objective.priority)}`}>
                        {objective.priority}
                      </span>
                    </div>
                  ))
                }
                {objectives.filter(obj => obj.employeeId === selectedEmployee.id).length === 0 && (
                  <p className="text-gray-500 text-center py-4">Nu există obiective active</p>
                )}
              </div>
            </div>

            <div className="flex justify-end space-x-3 pt-4 border-t dark:border-gray-700">
              <button
                onClick={() => setShowFeedbackModal(false)}
                className="px-4 py-2 text-gray-600 bg-gray-100 hover:bg-gray-200 dark:bg-gray-700 dark:text-gray-400 dark:hover:bg-gray-600 rounded-lg transition-colors"
              >
                Închide
              </button>
              <button className="px-4 py-2 bg-primary-600 text-white hover:bg-primary-700 rounded-lg transition-colors">
                <Edit className="w-4 h-4 inline mr-2" />
                Adaugă Feedback
              </button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
};

export default EvaluarePerformanta;